import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { api, formatPrice } from "../lib/api";
import { MapPin, Bed, Bath, Square, ArrowLeft, Zap, Check, Phone, Mail } from "lucide-react";
import { toast, Toaster } from "sonner";

const PropertyDetailPage = () => {
  const { id } = useParams();
  const { lang, t } = useLang();
  const [property, setProperty] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setProperty(r.data)).catch(() => setProperty(null));
  }, [id]);

  if (!property) {
    return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-[#5C544B]">…</div>;
  }

  const title = lang === "it" ? property.title_it : property.title_en;
  const desc = lang === "it" ? property.description_it : property.description_en;
  const features = lang === "it" ? property.features_it : property.features_en;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contacts", { ...form, property_id: property.id });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="property-detail-page" className="fade-in">
      <Toaster position="top-right" richColors />
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-8">
        <Link to="/immobili" className="inline-flex items-center gap-2 text-sm text-[#5C544B] hover:text-[#8B5A3C] transition-colors" data-testid="back-to-list">
          <ArrowLeft size={15} /> {t.property.backToList}
        </Link>
      </div>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 mt-6">
        <div className="grid lg:grid-cols-4 gap-3 lg:h-[520px]">
          <div className="lg:col-span-3 rounded-2xl overflow-hidden bg-[#F3E6DA] aspect-[16/10] lg:aspect-auto">
            <img src={property.images?.[activeImage]} alt={title} className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-1 gap-3">
            {property.images?.slice(0, 3).map((img, i) => (
              <button
                key={i}
                onClick={() => setActiveImage(i)}
                data-testid={`gallery-thumb-${i}`}
                className={`rounded-2xl overflow-hidden aspect-[4/3] lg:aspect-auto lg:flex-1 border-2 transition-all ${activeImage === i ? "border-[#8B5A3C]" : "border-transparent opacity-80 hover:opacity-100"}`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="bg-[#F3E6DA] text-[#8B5A3C] text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full">
              {property.contract === "sale" ? t.property.forSale : t.property.forRent}
            </span>
            <span className="bg-[#F3E6DA] text-[#5E6B56] text-[11px] font-bold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full">
              {t.filters[property.type] || property.type}
            </span>
          </div>

          <h1 data-testid="property-title" className="font-display text-4xl sm:text-5xl font-medium leading-tight tracking-tight text-[#2C2621] mb-3">
            {title}
          </h1>
          <div className="flex items-center gap-1.5 text-[#5C544B] mb-8">
            <MapPin size={16} className="text-[#8B5A3C]" />
            <span>{property.zone}, {property.city}</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
            {[
              { icon: Bed, label: t.property.bedrooms, value: property.bedrooms },
              { icon: Bath, label: t.property.bathrooms, value: property.bathrooms },
              { icon: Square, label: t.property.area, value: `${property.area_sqm} m²` },
              { icon: Zap, label: t.property.energy, value: property.energy_class },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-[#E8DFD8] rounded-2xl p-5">
                <s.icon size={18} className="text-[#8B5A3C] mb-2" />
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#8A8178]">{s.label}</div>
                <div className="font-display text-xl text-[#2C2621] mt-1">{s.value}</div>
              </div>
            ))}
          </div>

          <p className="text-[#5C544B] leading-relaxed mb-10 whitespace-pre-line">{desc}</p>

          {features?.length > 0 && (
            <div>
              <h3 className="font-display text-2xl text-[#2C2621] mb-5">{t.property.features}</h3>
              <ul className="grid sm:grid-cols-2 gap-3">
                {features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#5C544B]">
                    <Check size={16} className="text-[#8B5A3C] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sidebar contact form */}
        <aside className="lg:sticky lg:top-28 lg:self-start">
          <div className="bg-[#F3E6DA] rounded-2xl p-7 mb-4">
            <div className="text-xs uppercase tracking-[0.22em] text-[#8B5A3C] font-bold mb-2">
              {property.contract === "sale" ? t.property.forSale : t.property.forRent}
            </div>
            <div data-testid="property-price" className="font-display text-4xl text-[#2C2621]">
              {formatPrice(property.price, property.contract, lang)}
            </div>
          </div>

          <form onSubmit={submit} data-testid="property-contact-form" className="bg-white border border-[#E8DFD8] rounded-2xl p-6 space-y-4">
            <h3 className="font-display text-xl text-[#2C2621]">{t.property.contactCta}</h3>
            <input required placeholder={t.contact.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#FDFBF7] border border-[#E8DFD8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5A3C]/30 focus:border-[#8B5A3C]" data-testid="contact-name" />
            <input required type="email" placeholder={t.contact.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-[#FDFBF7] border border-[#E8DFD8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5A3C]/30 focus:border-[#8B5A3C]" data-testid="contact-email" />
            <input placeholder={t.evaluation.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-[#FDFBF7] border border-[#E8DFD8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5A3C]/30 focus:border-[#8B5A3C]" data-testid="contact-phone" />
            <textarea required rows={4} placeholder={t.contact.message} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-[#FDFBF7] border border-[#E8DFD8] rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B5A3C]/30 focus:border-[#8B5A3C] resize-none" data-testid="contact-message" />
            <button disabled={submitting} type="submit" data-testid="contact-submit" className="w-full bg-[#8B5A3C] text-white hover:bg-[#704830] disabled:opacity-60 transition-colors rounded-full py-3 font-medium">
              {submitting ? "…" : t.property.contactCta}
            </button>
            <div className="flex items-center gap-4 pt-2 text-xs text-[#5C544B]">
              <a href="tel:+393757380501" className="flex items-center gap-1.5 hover:text-[#8B5A3C]"><Phone size={13} />375 738 0501</a>
              <a href="mailto:segreteria@lasalaimmobiliare.it" className="flex items-center gap-1.5 hover:text-[#8B5A3C]"><Mail size={13} />Email</a>
            </div>
          </form>
        </aside>
      </section>
    </div>
  );
};

export default PropertyDetailPage;
